// src/components/serverSetup/NFTConfiguration.js

import React, { useContext, useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  TextField,
  Box,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Badge,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
} from '@mui/icons-material';
import TraitSelectionModal from './TraitSelectionModal';
import CreateRoleModal from './CreateRoleModal'; // Import the CreateRoleModal
import { AuthContext } from '../../context/AuthContext';

const NFTConfiguration = ({
  collectionList = [],
  roleConfigurations = [],
  setRoleConfigurations = () => {},
  traits = [],
  setTraits = () => {},
  isTraitModalOpen = false,
  setIsTraitModalOpen = () => {},
  selectedTraitIndex = null,
  setSelectedTraitIndex = () => {},
  roles = [], // Ensure roles is passed as a prop
  refreshRoles,
}) => {
  const { selectedGuild, accessToken } = useContext(AuthContext);

  // State for Trait Selection Modal
  const [currentRoleConfigIndex, setCurrentRoleConfigIndex] = useState(null);
  const [currentRequirementIndex, setCurrentRequirementIndex] = useState(null);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
 

  // State for Create Role Modal
  const [isCreateRoleModalOpen, setIsCreateRoleModalOpen] = useState(false);

  // Callback to refresh roles after creating a new role
  const handleRoleCreated = () => {
    // Notify the user that a new role has been created
    setSnackbar({
      open: true,
      message: 'New role created successfully! Please select it from the dropdown.',
      severity: 'success',
    });
    // Trigger roles refresh
    if (refreshRoles) {
      refreshRoles();
    }
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Function to handle role selection change
  const handleRoleChange = (roleConfigIndex, value) => {
    setRoleConfigurations((prev) => {
      const updated = [...prev];
      updated[roleConfigIndex].roleId = value;

      if (value === 'create_new_role') {
        // Open the Create Role Modal
        setIsCreateRoleModalOpen(true);
        updated[roleConfigIndex].roleId = ''; // Reset the roleId to allow selection after creation
      }

      return updated;
    });
  };

  // Function to open Trait Selection Modal
  const openTraitModal = (roleConfigIndex, requirementIndex) => {
    setCurrentRoleConfigIndex(roleConfigIndex);
    setCurrentRequirementIndex(requirementIndex);
    setIsTraitModalOpen(true);
  };

  // Function to close Trait Selection Modal
  const closeTraitModal = () => {
    setIsTraitModalOpen(false);
    setCurrentRoleConfigIndex(null);
    setCurrentRequirementIndex(null);
  };

  // Function to handle requirement type change
  const handleRequirementTypeChange = (roleConfigIndex, type) => {
    setRoleConfigurations((prev) => {
      const updated = [...prev];
      updated[roleConfigIndex].type = type;

      if (type === 'combined') {
        // Initialize combinedRequirement if switching to 'combined'
        updated[roleConfigIndex].combinedRequirement = {
          collectionAddresses: [],
          combinedCount: '',
        };
        // Clear individual requirements if any
        updated[roleConfigIndex].requirements = [];
      } else {
        // Clear combinedRequirement if switching to 'individual'
        delete updated[roleConfigIndex].combinedRequirement;
        // Initialize individual requirements if not present
        if (!updated[roleConfigIndex].requirements) {
          updated[roleConfigIndex].requirements = [];
        }
      }

      return updated;
    });
  };

  // Function to handle combined count change
  const handleCombinedCountChange = (roleConfigIndex, value) => {
    setRoleConfigurations((prev) => {
      const updated = [...prev];
      if (updated[roleConfigIndex].combinedRequirement) {
        updated[roleConfigIndex].combinedRequirement.combinedCount = Number(value);
      }
      return updated;
    });
  };

  // Function to handle collection selection for combined requirement
  const handleCombinedCollectionsChange = (roleConfigIndex, selectedCollections) => {
    setRoleConfigurations((prev) => {
      const updated = [...prev];
      if (updated[roleConfigIndex].combinedRequirement) {
        updated[roleConfigIndex].combinedRequirement.collectionAddresses = selectedCollections;
      }
      return updated;
    });
  };

  return (
    <Accordion
      sx={{
        mt: 2,
        backgroundColor: '#081529',
        backgroundImage: 'linear-gradient(145deg, #081529, #12203d)',
        '&.Mui-expanded': {
          backgroundColor: '#081529',
          backgroundImage: 'linear-gradient(145deg, #081529, #12203d)',
        },
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.5)',
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: '#A1D1F0' }} />}
        sx={{
          backgroundColor: '#081529',
          color: '#E0E4ED',
          fontFamily: 'Poppins, sans-serif',
        }}
      >
        <Typography sx={{ color: '#A1D1F0', fontFamily: 'Poppins, sans-serif' }}>
          NFT Configuration
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box>
          {/* Role Configurations */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ color: '#A1D1F0' }}>
              Role Configurations Based on NFT Count or Traits
            </Typography>
            {roleConfigurations.map((roleConfig, roleIndex) => (
              <Box
                key={roleIndex}
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: '#132A47',
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                }}
              >
                {/* Role Summary and Remove Button */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    sx={{ color: '#E0E4ED', fontFamily: 'Poppins, sans-serif' }}
                  >
                    Role Configuration {roleIndex + 1}
                  </Typography>
                  <IconButton
                    onClick={() => {
                      const updated = [...roleConfigurations];
                      updated.splice(roleIndex, 1);
                      setRoleConfigurations(updated);
                    }}
                    sx={{
                      color: '#FF6B6B',
                    }}
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>

                {/* Role Dropdown */}
                <FormControl fullWidth variant="outlined" margin="normal">
                  <InputLabel
                    id={`role-dropdown-label-${roleIndex}`}
                    sx={{
                      color: '#D1E5F0', // Lighter color for the label
                      '&.Mui-focused': {
                        color: '#A1D1F0', // Brand color when focused
                      },
                    }}
                  >
                    Select Role
                  </InputLabel>
                  <Select
                    labelId={`role-dropdown-label-${roleIndex}`}
                    value={roleConfig.roleId}
                    onChange={(e) => handleRoleChange(roleIndex, e.target.value)}
                    label="Select Role"
                    sx={{
                      color: '#E0E4ED',
                      backgroundColor: '#132A47',
                      borderRadius: '8px',
                      fontFamily: 'Poppins, sans-serif',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#607080',
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#9AC0CD',
                      },
                      '& .MuiSelect-icon': {
                        color: '#A1D1F0',
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          backgroundColor: '#081529', // Background color for the dropdown options
                          color: '#E0E4ED', // Text color for dropdown options
                        },
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em style={{ color: '#E0E4ED' }}>Select a Role</em>
                    </MenuItem>
                    {roles.map((role) => (
                      <MenuItem
                        key={role.id}
                        value={role.id}
                        sx={{
                          color: '#E0E4ED', // Color for each menu item text
                          fontFamily: 'Poppins, sans-serif',
                          '&:hover': {
                            backgroundColor: '#0D1F3A', // Slightly different background on hover
                          },
                        }}
                      >
                        {role.name}
                      </MenuItem>
                    ))}
                    <MenuItem value="create_new_role">
                      <em style={{ color: '#E0E4ED' }}>Create New Role</em>
                    </MenuItem>
                  </Select>
                </FormControl>

                {/* Handle Create New Role Selection */}
                {roleConfig.roleId === 'create_new_role' && (
                  <Box sx={{ mt: 2 }}>
                    {/* Open the CreateRoleModal */}
                    <Button
                      variant="outlined"
                      sx={{
                        color: '#A1D1F0',
                        borderColor: '#A1D1F0',
                        textTransform: 'none',
                        fontFamily: 'Poppins, sans-serif',
                        '&:hover': {
                          borderColor: '#9AC0CD',
                          backgroundColor: 'rgba(154, 192, 205, 0.1)',
                        },
                      }}
                      startIcon={<AddIcon />}
                      onClick={() => setIsCreateRoleModalOpen(true)}
                    >
                      Create New Role
                    </Button>
                  </Box>
                )}

                {/* Requirement Type Selection */}
                <Box sx={{ mt: 2 }}>
                  <FormControl component="fieldset">
                    <Typography variant="subtitle2" sx={{ color: '#A1D1F0', mb: 1 }}>
                      Requirement Type
                    </Typography>
                    <RadioGroup
                      row
                      value={roleConfig.type || 'individual'}
                      onChange={(e) => handleRequirementTypeChange(roleIndex, e.target.value)}
                    >
                      <FormControlLabel
                        value="individual"
                        control={<Radio />}
                        label="Individual Collections"
                        sx={{ color: '#E0E4ED' }}
                      />
                      <FormControlLabel
                        value="combined"
                        control={<Radio />}
                        label="Combined Collections"
                        sx={{ color: '#E0E4ED' }}
                      />
                    </RadioGroup>
                  </FormControl>
                </Box>

                {/* Render Requirements Based on Type */}
                {roleConfig.type === 'individual' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#A1D1F0', mb: 1 }}>
                      Collection Requirements
                    </Typography>
                    {roleConfig.requirements.map((req, reqIndex) => (
                      <Box
                        key={reqIndex}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 2,
                          mt: 1,
                          flexWrap: 'wrap',
                          width: '100%', // Ensure full width to prevent wrapping
                        }}
                      >
                        {/* Collection Name Select */}
                        <FormControl sx={{ width: '50%' }}>
                          <InputLabel
                            sx={{
                              color: '#D1E5F0',
                              fontFamily: 'Poppins, sans-serif',
                            }}
                          >
                            Collection Name
                          </InputLabel>
                          <Select
                            value={req.collectionAddress}
                            onChange={(e) =>
                              setRoleConfigurations((prev) => {
                                const updated = [...prev];
                                updated[roleIndex].requirements[reqIndex].collectionAddress =
                                  e.target.value;
                                return updated;
                              })
                            }
                            label="Collection Name"
                            sx={{
                              color: '#E0E4ED',
                              backgroundColor: '#132A47',
                              borderRadius: '8px',
                              fontFamily: 'Poppins, sans-serif',
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#607080',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: '#9AC0CD',
                              },
                              '& .MuiSelect-icon': {
                                color: '#A1D1F0',
                              },
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  backgroundColor: '#081529', // Background color for the dropdown options
                                  color: '#E0E4ED', // Text color for dropdown options
                                },
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em style={{ color: '#E0E4ED' }}>Select a Collection</em>
                            </MenuItem>
                            {collectionList.map((collection) => (
                              <MenuItem
                                key={collection.collectionAddress}
                                value={collection.collectionAddress}
                                sx={{
                                  color: '#E0E4ED', // Color for each menu item text
                                  fontFamily: 'Poppins, sans-serif',
                                  '&:hover': {
                                    backgroundColor: '#0D1F3A', // Slightly different background on hover
                                  },
                                }}
                              >
                                {collection.name || collection.collectionAddress}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>

                        {/* NFT Count TextField */}
                        <TextField
                          label="NFT Count"
                          value={req.count}
                          onChange={(e) =>
                            setRoleConfigurations((prev) => {
                              const updated = [...prev];
                              updated[roleIndex].requirements[reqIndex].count = Number(e.target.value);
                              return updated;
                            })
                          }
                          placeholder="Enter NFT Count"
                          type="number"
                          InputProps={{
                            style: {
                              color: '#E0E4ED',
                              backgroundColor: '#132A47',
                              borderRadius: '8px',
                            },
                          }}
                          InputLabelProps={{
                            style: {
                              color: '#D1E5F0',
                            },
                          }}
                          sx={{
                            width: '12%',
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': {
                                borderColor: '#607080',
                              },
                              '&:hover fieldset': {
                                borderColor: '#9AC0CD',
                              },
                            },
                          }}
                        />

                        {/* Select Traits Button with Badge */}
                        <Badge
                          badgeContent={req.traits && req.traits.length > 0 ? req.traits.length : 0}
                          color="primary"
                          invisible={!req.traits || req.traits.length === 0}
                          overlap="rectangular"
                          sx={{ mr: 1 }} // Add right margin for spacing
                        >
                          <Button
                            variant="outlined"
                            sx={{
                              color: '#A1D1F0',
                              borderColor: '#A1D1F0',
                              textTransform: 'none',
                              fontFamily: 'Poppins, sans-serif',
                              minWidth: '120px', // Ensure consistent button width
                              '&:hover': {
                                borderColor: '#9AC0CD',
                                backgroundColor: 'rgba(154, 192, 205, 0.1)',
                              },
                            }}
                            onClick={() => {
                              openTraitModal(roleIndex, reqIndex);
                            }}
                            disabled={!req.collectionAddress} // Optional: Disable if collection not selected
                          >
                            Select Traits
                          </Button>
                        </Badge>

                        {/* Remove Requirement Icon */}
                        <IconButton
                          onClick={() => {
                            setRoleConfigurations((prev) => {
                              const updated = [...prev];
                              updated[roleIndex].requirements.splice(reqIndex, 1);
                              return updated;
                            });
                          }}
                          sx={{
                            color: '#FF6B6B',
                          }}
                        >
                          <RemoveIcon />
                        </IconButton>

                        {/* Display Selected Traits as Chips */}
                        {req.traits && req.traits.length > 0 && (
                          <Box
                            sx={{
                              mt: 0,
                              mb: 1,
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 1,
                              width: '100%',
                            }}
                          >
                            {req.traits.map((trait, traitIndex) => (
                              <Chip
                                key={`${trait}-${traitIndex}`} // Ensure unique keys
                                label={trait} // Directly use the trait string
                                onDelete={() => {
                                  setRoleConfigurations((prev) => {
                                    const updated = [...prev];
                                    updated[roleIndex].requirements[reqIndex].traits.splice(
                                      traitIndex,
                                      1
                                    );
                                    return updated;
                                  });
                                }}
                                color="primary"
                                variant="outlined"
                                size="small"
                                sx={{
                                  fontSize: '0.875rem',
                                  padding: '4px 8px',
                                }}
                              />
                            ))}
                          </Box>
                        )}
                      </Box>
                    ))}

                    {/* Add Collection Requirement Button Wrapped in Box */}
                    <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-start' }}>
                      <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        sx={{
                          color: '#A1D1F0',
                          borderColor: '#A1D1F0',
                          textTransform: 'none',
                          fontFamily: 'Poppins, sans-serif',
                          '&:hover': {
                            borderColor: '#9AC0CD',
                            backgroundColor: 'rgba(154, 192, 205, 0.1)',
                          },
                        }}
                        onClick={() =>
                          setRoleConfigurations((prev) => {
                            const updated = [...prev];
                            updated[roleIndex].requirements.push({
                              collectionAddress: '',
                              count: '',
                              traits: [],
                            });
                            return updated;
                          })
                        }
                      >
                        Add Collection Requirement
                      </Button>
                    </Box>
                  </Box>
                )}

                {roleConfig.type === 'combined' && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" sx={{ color: '#A1D1F0', mb: 1 }}>
                      Combined Collection Requirements
                    </Typography>
                    {/* Select Multiple Collections */}
                    <FormControl fullWidth variant="outlined" margin="normal">
                      <InputLabel
                        id={`combined-collections-label-${roleIndex}`}
                        sx={{
                          color: '#D1E5F0',
                          '&.Mui-focused': {
                            color: '#A1D1F0',
                          },
                        }}
                      >
                        Select Collections
                      </InputLabel>
                      <Select
                        labelId={`combined-collections-label-${roleIndex}`}
                        multiple
                        value={roleConfig.combinedRequirement.collectionAddresses || []}
                        onChange={(e) => handleCombinedCollectionsChange(roleIndex, e.target.value)}
                        label="Select Collections"
                        renderValue={(selected) =>
                          selected
                            .map((addr) => {
                              const collection = collectionList.find(
                                (col) => col.collectionAddress === addr
                              );
                              return collection ? collection.name : addr;
                            })
                            .join(', ')
                        }
                        sx={{
                          color: '#E0E4ED',
                          backgroundColor: '#132A47',
                          borderRadius: '8px',
                          fontFamily: 'Poppins, sans-serif',
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#607080',
                          },
                          '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: '#9AC0CD',
                          },
                          '& .MuiSelect-icon': {
                            color: '#A1D1F0',
                          },
                        }}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              backgroundColor: '#081529', // Background color for the dropdown options
                              color: '#E0E4ED', // Text color for dropdown options
                            },
                          },
                        }}
                      >
                        {collectionList.map((collection) => (
                          <MenuItem
                            key={collection.collectionAddress}
                            value={collection.collectionAddress}
                            sx={{
                              color: '#E0E4ED', // Color for each menu item text
                              fontFamily: 'Poppins, sans-serif',
                              '&:hover': {
                                backgroundColor: '#0D1F3A', // Slightly different background on hover
                              },
                            }}
                          >
                            {collection.name || collection.collectionAddress}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    {/* Combined NFT Count */}
                    <TextField
                      label="Combined NFT Count"
                      value={roleConfig.combinedRequirement.combinedCount || ''}
                      onChange={(e) =>
                        handleCombinedCountChange(roleIndex, e.target.value)
                      }
                      placeholder="Enter Combined NFT Count"
                      type="number"
                      InputProps={{
                        style: {
                          color: '#E0E4ED',
                          backgroundColor: '#132A47',
                          borderRadius: '8px',
                        },
                      }}
                      InputLabelProps={{
                        style: {
                          color: '#D1E5F0',
                        },
                      }}
                      sx={{
                        width: '100%',
                        mt: 2,
                        '& .MuiOutlinedInput-root': {
                          '& fieldset': {
                            borderColor: '#607080',
                          },
                          '&:hover fieldset': {
                            borderColor: '#9AC0CD',
                          },
                        },
                      }}
                    />
                  </Box>
                )}
              </Box>
            ))}

            {/* Add Role Configuration Button */}
            <Box sx={{ mt: 2 }}>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                sx={{
                  mt: 2,
                  color: '#A1D1F0',
                  borderColor: '#A1D1F0',
                  textTransform: 'none',
                  fontFamily: 'Poppins, sans-serif',
                  '&:hover': {
                    borderColor: '#9AC0CD',
                    backgroundColor: 'rgba(154, 192, 205, 0.1)',
                  },
                }}
                onClick={() =>
                  setRoleConfigurations((prev) => [
                    ...prev,
                    { roleId: '', type: 'individual', requirements: [] },
                  ])
                }
              >
                Add Role Configuration
              </Button>
            </Box>
          </Box>

          {/* Trait Selection Modal */}
          <TraitSelectionModal
            open={isTraitModalOpen}
            onClose={closeTraitModal} // Using a dedicated function to close the modal
            roleConfigIndex={currentRoleConfigIndex} // Pass the current role config index
            requirementIndex={currentRequirementIndex} // Pass the current requirement index
            traits={traits} // The list of traits fetched
            roleConfigurations={roleConfigurations}
            setRoleConfigurations={setRoleConfigurations}
            onSaveSuccess={(message) => {
              setSnackbar({
                open: true,
                message: message,
                severity: 'success',
              });
            }}
          />

          {/* Create Role Modal */}
          <CreateRoleModal
            open={isCreateRoleModalOpen}
            onClose={() => setIsCreateRoleModalOpen(false)}
            accessToken={accessToken}
            selectedGuild={selectedGuild}
            onRoleCreated={handleRoleCreated}
          />

          {/* Snackbar for user feedback */}
          <Snackbar
            open={snackbar.open}
            autoHideDuration={6000}
            onClose={handleSnackbarClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          >
            <Alert
              onClose={handleSnackbarClose}
              severity={snackbar.severity}
              sx={{ width: '100%' }}
              elevation={6}
              variant="filled"
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default NFTConfiguration;

